import React, { useState } from 'react';
import { Loader2, AlertCircle, Smartphone } from 'lucide-react';
import TelegramLoginWidget, { TelegramUser } from '@/components/TelegramLoginWidget';
import {
  storeBrowserAuthUser,
  BrowserAuthUser,
  mockTelegramEnvForBrowserUser,
} from '@/utils/telegram-environment';
import { TokenManager } from '@/utils/api-client';
import axios from 'axios';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://main-api-production-1bb6.up.railway.app/api';

interface BrowserLoginPageProps {
  onLoginSuccess: () => void;
}

interface LoginWidgetResponse {
  token: string;
  user: BrowserAuthUser;
}

/**
 * Browser Login Page
 * 
 * Shown when the miniapp is accessed from a regular browser instead of Telegram.
 * Displays a Telegram Login Widget for authentication.
 */
const BrowserLoginPage: React.FC<BrowserLoginPageProps> = ({ onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const botName = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'triggerdotfun_bot';

  const handleTelegramAuth = async (telegramUser: TelegramUser) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[BrowserLogin] Authenticating with Telegram widget data:', telegramUser);

      // Convert TelegramUser to widget data format for the API
      const widgetData: Record<string, string> = {
        id: telegramUser.id.toString(),
        auth_date: telegramUser.auth_date.toString(),
        hash: telegramUser.hash,
      };
      
      if (telegramUser.first_name) widgetData.first_name = telegramUser.first_name;
      if (telegramUser.last_name) widgetData.last_name = telegramUser.last_name;
      if (telegramUser.username) widgetData.username = telegramUser.username;
      if (telegramUser.photo_url) widgetData.photo_url = telegramUser.photo_url;

      // Call the backend to validate and get JWT token
      const response = await axios.post<LoginWidgetResponse>(
        `${API_BASE_URL}/auth/validate-widget`,
        widgetData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('[BrowserLogin] Auth response:', response.data);

      const { token, user } = response.data;

      // Store the JWT token
      TokenManager.setToken(token);

      // Store the browser auth user data
      storeBrowserAuthUser(user);

      // Mock the Telegram SDK with the authenticated user data
      mockTelegramEnvForBrowserUser(user);

      console.log('[BrowserLogin] Successfully authenticated browser user:', user.id);

      // Notify parent component of successful login
      onLoginSuccess();
    } catch (err) {
      console.error('[BrowserLogin] Authentication error:', err);
      
      let errorMessage = 'Failed to authenticate. Please try again.';
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          errorMessage = 'Authentication failed. The login data may be expired. Please try again.';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6">
      {/* Background gradient effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/3 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-md w-full">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-accent/20">
            <Smartphone className="w-10 h-10 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            Trigger
          </h1>
        </div>

        {/* Login Card */}
        <div className="bg-secondary rounded-2xl p-8 border border-border">
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              Sign in
            </h2>
            <p className="text-text-secondary text-sm">
              Sign in with your Telegram account to continue
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Login Widget Container */}
          <div className="flex justify-center">
            {isLoading ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
                <p className="text-text-secondary text-sm">Authenticating...</p>
              </div>
            ) : (
              <TelegramLoginWidget
                botName={botName}
                onAuth={handleTelegramAuth}
                buttonSize="large"
                cornerRadius={8}
              />
            )}
          </div>
        </div>

        {/* Info Text */}
        <div className="mt-6 text-center">
          <p className="text-text-muted text-xs">
            For the best experience, open this app directly in Telegram.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BrowserLoginPage;

